import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getBlogPostBySlugFromApi, getBlogPosts } from "../api/apiCalls";
import NotFound from "./NotFound";
import { getBlogImage, getBlogImageAlt } from "../utils/blogImages";
import {
  createArticleSchema,
  createBreadcrumbSchema,
  usePageSeo,
} from "../utils/seo";

const formatPostDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setPost(null);
    setRelatedPosts([]);

    Promise.all([getBlogPostBySlugFromApi(slug), getBlogPosts()])
      .then(([apiPost, apiPosts]) => {
        if (!isMounted) {
          return;
        }

        setPost(apiPost);
        setRelatedPosts(
          Array.isArray(apiPosts)
            ? apiPosts.filter((item) => item.slug !== slug).slice(0, 2)
            : []
        );
      })
      .catch(() => {
        if (isMounted) {
          setPost(null);
          setRelatedPosts([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const structuredData = post
    ? [
        createArticleSchema(post),
        createBreadcrumbSchema([
          { name: "Почетна", path: "/" },
          { name: "Блог", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]),
      ]
    : null;

  usePageSeo({
    title: post?.metaTitle || "Buzz Mobile блог",
    description:
      post?.metaDescription ||
      "Совети од Buzz Mobile за телефони, сервис и мобилни додатоци.",
    canonicalPath: post ? `/blog/${post.slug}` : "/blog",
    image: post?.heroImage,
    type: "article",
    structuredData,
  });

  const postDate = formatPostDate(
    post?.dateModified ||
      post?.updatedAt ||
      post?.datePublished ||
      post?.publishAt ||
      post?.createdAt
  );

  if (!post) {
    return isLoading ? (
      <main className="blog-post-page">
        <div className="container blog-post-loading">Се вчитува блог пост...</div>
      </main>
    ) : (
      <NotFound />
    );
  }

  return (
    <main className="blog-post-page">
      <article>
        <header className="blog-post-hero">
          <div className="container blog-post-hero__inner">
            <div className="blog-post-hero__copy">
              <Link className="blog-back-link" to="/blog">
                <ArrowBackIcon fontSize="small" /> Сите водичи
              </Link>
              <span className="blog-eyebrow">{post.category}</span>
              <h1>{post.title}</h1>
              <p>{post.metaDescription}</p>
              <div className="blog-post-meta">
                <span>
                  <AccessTimeIcon fontSize="small" />
                  {post.readingTime}
                </span>
                <time dateTime={postDate}>
                  <CalendarMonthIcon fontSize="small" />
                  Ажурирано: {postDate}
                </time>
              </div>
            </div>
            <div className="blog-post-hero__image">
              <img
                className={!post.heroImage ? "blog-image--fallback" : undefined}
                src={getBlogImage(post)}
                alt={getBlogImageAlt(post)}
              />
            </div>
          </div>
        </header>

        <div className="container blog-post-layout">
          <aside className="blog-post-sidebar" aria-label="Брз одговор">
            <div className="blog-answer-card">
              <span>Брз одговор</span>
              <p>{post.quickAnswer}</p>
            </div>
            <div className="blog-checklist-card">
              <span>Проверете пред одлука</span>
              <ul>
                {(post.checklist || []).map((item) => (
                  <li key={item}>
                    <CheckCircleOutlineIcon fontSize="small" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="blog-post-content">
            <p className="blog-post-intro">{post.intro}</p>

            {(post.sections || []).map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {(section.body || []).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            ))}

            <section className="blog-faq-section">
              <h2>Најчести прашања</h2>
              {(post.faqs || []).map((faq) => (
                <details key={faq.question} className="blog-faq">
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </section>

            <section className="blog-related-links">
              <h2>Поврзани линкови</h2>
              <div>
                {(post.relatedLinks || []).map((link) => (
                  <Link key={link.path} to={link.path}>
                    {link.label} <ArrowForwardIcon fontSize="small" />
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 ? (
        <section className="container blog-more-posts">
          <div className="blog-section-heading">
            <span className="blog-eyebrow">Поврзани водичи</span>
            <h2>Продолжете со читање</h2>
          </div>
          <div className="blog-grid blog-grid--compact">
            {relatedPosts.map((relatedPost) => (
              <Link
                className="blog-card"
                key={relatedPost.slug}
                to={`/blog/${relatedPost.slug}`}
              >
                <div className="blog-card__image">
                  <img
                    className={!relatedPost.heroImage ? "blog-image--fallback" : undefined}
                    src={getBlogImage(relatedPost)}
                    alt={getBlogImageAlt(relatedPost)}
                  />
                </div>
                <div className="blog-card__body">
                  <span className="blog-chip">{relatedPost.category}</span>
                  <h2>{relatedPost.title}</h2>
                  <p>{relatedPost.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}

export default BlogPost;
